/**
 * Engine — the system-wide scheduler config screen (WSRPDM-0014, contract
 * §2). Start/stop card (4 live flags) + the 28-knob config form (Cadences /
 * Retention / Tuning), tabbed (WSRPDM-0074) and driven off
 * `src/domain/scheduleKnobs.ts`.
 *
 * `globalEnabled` is read-only here — it's the Tenants screen's master
 * switch (contract §1.3); `StartStopCard` only displays it.
 */
import { Alert, Box, Button, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useScheduleConfig } from './engine/useScheduleConfig';
import { StartStopCard } from './engine/StartStopCard';
import { EngineConfigForm } from './engine/EngineConfigForm';

export function Engine() {
  const { t } = useCopy();
  const { config, loading, error, refetch, applyView } = useScheduleConfig();

  return (
    <Box>
      <Typography variant="h1" component="h1" gutterBottom>
        {t('page.engine.title', 'Engine')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('page.engine.subtitle', 'Engine and schedule configuration.')}
      </Typography>

      {loading ? <LoadingSpinner /> : null}

      {!loading && error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('engine.loadError', 'Could not load engine configuration.')}
        </Alert>
      ) : null}

      {!loading && config ? (
        <EngineConfigForm
          config={config}
          onSaved={applyView}
          startStop={<StartStopCard config={config} onSaved={applyView} />}
        />
      ) : null}
    </Box>
  );
}
